import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import Card from '@/views/dashboard/_components/card/card.vue'
import PendingApprovalList from '@/views/dashboard/_components/pending-approval-list/pending-approval-list.vue'
import { PendingApprovalItem } from '@/views/dashboard/_components/pending-approval-list/pending-approval-list.vue'
import FlowRecordSidebar from '@/views/dashboard/approval-center/_components/flow-record-sidebar.vue'
import { ApprovalListService } from '@/views/dashboard/approval-center/service'
import {
  ApiApprovalTypeEnum,
  ApiApprovalStatusEnum,
  ApprovalHistoryDataItem,
  ApprovalListDataItem
} from '@/views/dashboard/approval-center/types'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { formatTimeStamp } from '@/utils/time'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

const TAB_PENDING = 'pending'
const TAB_FLOW_RECORD = 'flow'

const LIST_PAGE_SIZE = 5


export default /*#__PURE__*/_defineComponent({
  __name: 'approval-section-card',
  setup(__props) {

const { t } = useI18n()
const injector = useReflectiveInjector([ApprovalListService])
const approvalListService = injector.get(ApprovalListService)
const servicePermissionService = injector.get(ServicePermissionService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.dlp_control)
const router = useRouter()

const activeTab = ref(TAB_PENDING)
const pendingCount = ref(0)
const pendingList = ref<PendingApprovalItem[]>([])
const pendingLoading = ref(false)

const flowRecords = ref<any[]>([])
const flowRecordLoading = ref(false)
const flowRecordCollapsed = ref(false)

const flowRecordLoadMoreConfig = computed(() => ({
  callback: () => { },
  loading: false,
  hasMore: false,
  disabled: true,
  threshold: 10,
  delay: 100
}))

function mapListItemToPending(item: ApprovalListDataItem): PendingApprovalItem {
  const applyTs = parseInt(item.apply_time, 10)
  return {
    id: item.id,
    record_id: item.id,
    description: item.content,
    user: item.applicant,
    group_name: item.group_name,
    apply_time: item.apply_time,
    approval_type: item.approval_type,
    time: Number.isFinite(applyTs)
      ? formatTimeStamp(applyTs * 1000, 'YYYY-MM-DD HH:mm')
      : ''
  }
}

function loadFlowRecords() {
  if (!hasPermission) {
    flowRecords.value = []
    return
  }
  flowRecordLoading.value = true
  approvalListService.getApprovalHistory({
    page_no: 1,
    page_size: LIST_PAGE_SIZE,
    approval_status: undefined,
    approval_type: IS_JP_ONLY_BUILD ? ApiApprovalTypeEnum.Peripheral : undefined
  }).then(res => {
    const approvalTypeNameMap: Record<number, string> = {
      [ApiApprovalTypeEnum.Peripheral]: t('approvalCenter.typeNamePeripheral'),
      [ApiApprovalTypeEnum.FileOutbound]: t('approvalCenter.typeNameSensitiveFileSend'),
      [ApiApprovalTypeEnum.Cascade]: t('approvalCenter.typeNameCascadeApproval'),
    }

    const approvalObjectNameMap: Record<string, string> = {
      'U盘': t('equipmentView.usb'),
      '便携设备': t('equipmentView.portable'),
    }

    flowRecords.value = (res.data || []).map((item: ApprovalHistoryDataItem) => {
      const typeName = approvalTypeNameMap[item.approval_type] || ''
      const objectName = approvalObjectNameMap[item.approval_object] || item.approval_object
      return {
        id: item.record_id,
        type: item.approval_type,
        time: formatTimeStamp(parseInt(item.operation_time, 10) * 1000, 'YYYY-MM-DD HH:mm'),
        title: `${typeName}[${objectName}]`,
        user: item.approver,
        status: item.approval_result === 'approved' ? 'approved' : 'rejected'
      }
    })
  }).finally(() => {
    flowRecordLoading.value = false
  })
}

async function loadPendingList() {
  if (!hasPermission) {
    pendingList.value = []
    pendingCount.value = 0
    return
  }
  pendingLoading.value = true
  try {
    const res = await approvalListService.getApprovalList({
      approval_type: IS_JP_ONLY_BUILD ? ApiApprovalTypeEnum.Peripheral : ApiApprovalTypeEnum.All,
      page_no: 1,
      page_size: LIST_PAGE_SIZE,
      approval_status: ApiApprovalStatusEnum.Pending
    })
    const rows = res.data || []
    pendingList.value = rows.map(mapListItemToPending)
    if (IS_JP_ONLY_BUILD) {
      const countRes = await approvalListService.getApprovalPendingCount()
      pendingCount.value = countRes.data?.peripheral_count || 0
    } else {
      pendingCount.value = parseInt(res.total || '0', 10)
    }
  } catch {
    pendingList.value = []
    pendingCount.value = 0
  } finally {
    pendingLoading.value = false
  }
}

function onPendingApprove(item: PendingApprovalItem) {
  const id = String(item.id ?? item.record_id ?? '')
  const approvalType = Number(item.approval_type)
  if (!id || !approvalType) return
  approvalListService.getApprovalApprove({
    approval_type: approvalType,
    record_id: id,
    is_agree: true
  }).then(() => {
    ElMessage.success(t('approvalCenter.operationSuccess'))
    loadPendingList()
    loadFlowRecords()
  })
}

function onPendingReject(item: PendingApprovalItem, reason?: string) {
  const id = String(item.id ?? item.record_id ?? '')
  const approvalType = Number(item.approval_type)
  if (!id || !approvalType) return
  approvalListService.getApprovalApprove({
    approval_type: approvalType,
    record_id: id,
    is_agree: false,
    reason: reason || ''
  }).then(() => {
    ElMessage.success(t('approvalCenter.operationSuccess'))
    loadPendingList()
    loadFlowRecords()
  })
}

function handleGoToApproval() {
  router.push('/dashboard/approval-center')
}

onMounted(() => {
  loadPendingList()
  loadFlowRecords()
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_tabs = _resolveComponent("el-tabs")!

  return (_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.$t('overviewZone.approvalCardTitle'),
    class: "approval-section-card-wrap"
  }, {
    titleAfter: _withCtx(() => [
      _createVNode(_component_el_tag, {
        size: "small",
        type: "primary"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('overviewZone.approvalListRecentFiveTag')), 1)
        ]),
        _: 1
      })
    ]),
    headerRight: _withCtx(() => [
      _createVNode(_component_el_button, {
        style: {"z-index":"2"},
        size: "mini",
        type: "primary",
        onClick: handleGoToApproval
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('menu.approvalCenter')), 1)
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_component_el_tabs, {
        modelValue: activeTab.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeTab).value = $event)),
        class: "approval-tabs"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_tab_pane, { name: TAB_PENDING }, {
            label: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t('approvalCenter.pending')) + "(" + _toDisplayString(pendingCount.value) + ")", 1)
            ]),
            default: _withCtx(() => [
              _createVNode(PendingApprovalList, {
                embed: "",
                list: pendingList.value,
                loading: pendingLoading.value,
                "max-items": 5,
                "data-type": "custom",
                title: _ctx.$t('approvalCenter.pending'),
                "approval-center-route": "/dashboard/approval-center",
                "approve-button-text": _ctx.$t('approvalCenter.agree'),
                "reject-button-text": _ctx.$t('approvalCenter.reject'),
                onApprove: onPendingApprove,
                onReject: onPendingReject
              }, null, 8, ["list", "loading", "title", "approve-button-text", "reject-button-text"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_tab_pane, {
            label: _ctx.$t('approvalCenter.flowRecord'),
            name: TAB_FLOW_RECORD
          }, {
            default: _withCtx(() => [
              _createVNode(FlowRecordSidebar, {
                embed: "",
                collapsed: flowRecordCollapsed.value,
                "onUpdate:collapsed": _cache[0] || (_cache[0] = ($event: any) => ((flowRecordCollapsed).value = $event)),
                disabledPopover: true,
                data: flowRecords.value,
                loading: flowRecordLoading.value,
                "load-more-config": flowRecordLoadMoreConfig.value
              }, null, 8, ["collapsed", "data", "loading", "load-more-config"])
            ]),
            _: 1
          }, 8, ["label"])
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})